// ###### IMPORTS
import { useMemo } from 'react';
import routes_api from '../../../connections/routes_api';
import { createCalendar, updateCalendar, deleteCalendar } from './planner_events_api';
import useOfflineFirstData from 'services/useOfflineFirstData';
import { toast } from 'react-hot-toast';

// ###### CUSTOM HOOK
export const usePlannerCalendars = () => {
  // Use offline-first hook for calendars
  const {
    data: calendarsData,
    isLoading,
    error,
    refresh: refreshCalendars
  } = useOfflineFirstData(routes_api.planner_calendars);

  // Sort calendars by name ascending (alphabetical)
  const calendars = useMemo(() => {
    if (!calendarsData || calendarsData.length === 0) return [];
    return [...calendarsData].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [calendarsData]);

  // ###### FETCH CALENDARS (kept for compatibility)
  const fetchCalendars = async () => {
    await refreshCalendars();
  };

  // ###### CREATE CALENDAR
  const handleCreateCalendar = async (calendarData) => {
    try {
      const newCalendar = await createCalendar(calendarData);
      toast.success('Calendar created successfully!');
      // Refresh calendars to get updated list
      await refreshCalendars();
      return newCalendar;
    } catch (err) {
      toast.error('Failed to create calendar');
      throw err;
    }
  };

  // ###### UPDATE CALENDAR
  const handleUpdateCalendar = async (id, calendarData) => {
    try {
      const updated = await updateCalendar(id, calendarData);
      toast.success('Calendar updated successfully!');
      // Refresh calendars to get updated list
      await refreshCalendars();
      return updated;
    } catch (err) {
      toast.error('Failed to update calendar');
      throw err;
    }
  };

  // ###### DELETE CALENDAR
  const handleDeleteCalendar = async (id) => {
    try {
      await deleteCalendar(id);
      toast.success('Calendar deleted successfully!');
      // Refresh calendars to get updated list
      await refreshCalendars();
    } catch (err) {
      toast.error('Failed to delete calendar');
      throw err;
    }
  };

  return {
    calendars,
    isLoading,
    error,
    fetchCalendars,
    createCalendar: handleCreateCalendar,
    updateCalendar: handleUpdateCalendar,
    deleteCalendar: handleDeleteCalendar
  };
};
